import { Box } from '@mantine/core';
import { AnimatePresence } from 'framer-motion';
import { type FC, Fragment, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { StepperSetupStep } from '@/stepper';
import { StepperSetupStepKey } from '@/stepper';
import type { StepperOnboardingFormValues } from '@/stepper/types.ts';
import {
	STEPPER_FIRST_STEP_NUMBER,
	STEPPER_SETUP_STEPS
} from '../../constants';
import { useStepperOnboarding } from '../../context/StepperOnboardingContext';
import { getStepperSetupStepStatus } from '../../utils';
import StepperStepCard from '../StepperStepCard';
import StepperStepTransitionProgressBar from '../StepperStepTransitionProgressBar';
import { CLASS_NAMES } from './classNames';
import StepperSetupStepContent from './components/StepperSetupStepContent';

const StepperSetupStepper: FC = () => {
	const {
		activeStepKey,
		goToStep,
		isAgentProgressActive,
		isAgentReady,
		isLocked,
		isSetupComplete,
		stepTransitionKey
	} = useStepperOnboarding();
	const { control } = useFormContext<StepperOnboardingFormValues>();
	const websiteUrl = useWatch({
		control,
		name: 'websiteUrl'
	});

	const isStepTransitionActive = Boolean(stepTransitionKey);
	const activeStepIndex = useMemo(
		() => STEPPER_SETUP_STEPS.findIndex((step) => step.key === activeStepKey),
		[activeStepKey]
	);
	const furthestReachedStepIndexRef = useRef(0);

	if (activeStepIndex > furthestReachedStepIndexRef.current) {
		furthestReachedStepIndexRef.current = activeStepIndex;
	}

	const unlockedStepIndex = Math.max(
		furthestReachedStepIndexRef.current,
		activeStepIndex
	);

	const handleSelectStep = (step: StepperSetupStep) => {
		if (isLocked || isStepTransitionActive) {
			return;
		}

		const selectedStepIndex = STEPPER_SETUP_STEPS.findIndex(
			(visibleStep) => visibleStep.key === step.key
		);

		if (selectedStepIndex > unlockedStepIndex) {
			return;
		}

		goToStep(step.key);
	};

	return (
		<Box
			className={CLASS_NAMES.setupList}
			data-testid="stepper-setup-stepper"
		>
			{STEPPER_SETUP_STEPS.map((step, index) => {
				const isMakeTestCallStep =
					step.key === StepperSetupStepKey.MakeTestCall;
				const isStepLocked = isLocked || isStepTransitionActive;

				return (
					<Fragment key={step.key}>
						<StepperStepCard
							isAgentProgressActive={isAgentProgressActive}
							isContentDimmed={false}
							isDisabled={
								index > unlockedStepIndex ||
								isStepTransitionActive ||
								(isMakeTestCallStep && !isAgentReady)
							}
							isLocked={isStepLocked}
							number={index + STEPPER_FIRST_STEP_NUMBER}
							onSelect={handleSelectStep}
							status={getStepperSetupStepStatus(
								step.key,
								activeStepKey,
								isSetupComplete
							)}
							step={step}
						>
							<StepperSetupStepContent
								isLocked={isStepLocked}
								step={step}
							/>
						</StepperStepCard>
						<AnimatePresence initial={false}>
							{stepTransitionKey === step.key && (
								<StepperStepTransitionProgressBar
									stepKey={stepTransitionKey}
									websiteUrl={websiteUrl}
								/>
							)}
						</AnimatePresence>
					</Fragment>
				);
			})}
		</Box>
	);
};

export default StepperSetupStepper;
